import { useState, useEffect, useCallback } from 'react'
import { useMutation, useQuery } from '@tanstack/react-query'
import { changeDealershipDetailsRequest } from '../../../api-client/identity'
import { getStates, getStatesKey } from '../../../api-client/account/get-states'
import { APIErrors } from '../../../api-client/enums'
import useValidation from '../../../hooks/use-validation'
import { useAuth } from '../../../contexts/auth'
import { Prefilled, PrefilledSelect } from '../../../components/inputs'
import { formatPhoneNumber } from '../../../utils/format-phone-number'
import { Button } from '../../../components/button'

const errorMessages = {
  invalidZipCode: 'Google API fail Error Text',
  countryNotSupported: 'Country is not supported by privauto',
}

export default function DealershipInformation({ onClose, onSuccess }) {
  const [{ userDetails, dealerDetails }, { authResync }] = useAuth()
  const [state, setState] = useState({
    userId: userDetails.userId,
    firstName: userDetails.firstName,
    lastName: userDetails.lastName,
    dealershipName: dealerDetails.dealershipName,
    phoneNumber: dealerDetails.phoneNumber,
    dealershipAddress: dealerDetails.dealershipAddress,
    dealershipCity: dealerDetails.dealershipCity,
    dealershipState: dealerDetails.dealershipState,
    dealershipZipCode: dealerDetails.dealershipZipCode,
    dealerUserType: dealerDetails.dealerUserType,
    funderName: dealerDetails.funderName,
    funderAddress: dealerDetails.funderAddress,
    funderCity: dealerDetails.funderCity,
    funderState: dealerDetails.funderState,
    funderZipCode: dealerDetails.funderZipCode,
    funderPhoneNumber: dealerDetails.funderPhoneNumber,
    funderEmail: dealerDetails.funderEmail,
  })
  const [mappedServerErrors, setMappedServerErrors] = useState({})
  const [isFormBeingEdited, setIsFormBeingEdited] = useState(false)
  const [isSavedSuccessfully, setIsSavedSuccessfully] = useState(false)

  const [showErrors, { isFormValid, errors }] = useValidation(state, validate)

  const { mutate, isLoading, error } = useMutation({
    mutationFn: changeDealershipDetailsRequest,
    onSuccess: () => {
      if (isFormBeingEdited) {
        setIsSavedSuccessfully(true)
      }
    },
    onSettled: () => {
      authResync()
    },
  })

  useEffect(() => {
    setMappedServerErrors(mapServerErrors(error?.errors))
  }, [error])

  const handleInputChange = (e) => {
    e.persist()
    setIsFormBeingEdited(true)
    setState((prev) => ({ ...prev, [e.target.name]: e.target.value }))
  }

  const handleButtonClick = (e) => {
    isFormBeingEdited ? updateDealershipDetails() : onClose()
  }

  const handleSelectChange = ({ value }, { name }) => {
    setIsFormBeingEdited(true)
    setState((p) => ({ ...p, [name]: value }))
  }

  const [statesOptions, setStatesOptions] = useState([])
  const [funderStatesOptions, setFunderStatesOptions] = useState([])

  const { data: { states = [] } = {}, isLoading: statesFetching } = useQuery({
    queryKey: [getStatesKey],
    queryFn: getStates,
  })

  useEffect(() => setStatesOptions(mapStatesOptions(states)), [states])
  useEffect(() => setFunderStatesOptions(mapFunderStatesOptions(states)), [states])

  const updateDealershipDetails = useCallback(() => {
    if (!isFormValid) {
      showErrors()
    } else {
      mutate({
        userId: state.userId,
        firstName: state.firstName,
        lastName: state.lastName,
        dealershipName: state.dealershipName,
        phoneNumber: state.phoneNumber ? state.phoneNumber.replace(/[^\d]/g, '') : '',
        dealershipAddress: state.dealershipAddress,
        dealershipCity: state.dealershipCity,
        dealershipState: state.dealershipState,
        dealershipZipCode: state.dealershipZipCode,
        dealerUserType: state.dealerUserType,
        funderName: state.funderName,
        funderAddress: state.funderAddress,
        funderCity: state.funderCity,
        funderState: state.funderState,
        funderZipCode: state.funderZipCode,
        funderPhoneNumber: state.funderPhoneNumber
          ? state.funderPhoneNumber.replace(/[^\d]/g, '')
          : '',
        funderEmail: state.funderEmail,
      })

      setIsSavedSuccessfully(true)
    }

    setIsFormBeingEdited(false)
  }, [mutate, isFormValid, showErrors, state])

  const dealerUserTypes = [
    { label: 'Dealer', value: 1 },
    { label: 'Fleet', value: 2 },
    { label: 'Wholesaler', value: 3 },
  ]

  return (
    <div className='wrapper'>
      <h1 className='title'>Dealership Information</h1>
      <p className='description'>Update your dealership details</p>
      <div className='inputFieldWrapper dealershipName'>
        <Prefilled
          type='text'
          name='dealershipName'
          placeholder='Dealership Name'
          error={errors.dealershipName}
          value={state.dealershipName}
          onChange={handleInputChange}
        />
      </div>
      <div className='inputFieldWrapper phoneNumber'>
        <Prefilled
          type='text'
          name='phoneNumber'
          placeholder='Phone Number'
          inputMode='numeric'
          maxLength='12'
          error={errors.phoneNumber}
          value={formatPhoneNumber(state.phoneNumber)}
          onChange={handleInputChange}
        />
      </div>
      <div className='inputFieldWrapper dealershipAddress'>
        <Prefilled
          type='text'
          name='dealershipAddress'
          placeholder='Address'
          error={errors.dealershipAddress}
          value={state.dealershipAddress}
          onChange={handleInputChange}
        />
      </div>

      <div className='inputFieldWrapper dealershipCity'>
        <Prefilled
          type='text'
          name='dealershipCity'
          placeholder='City'
          error={errors.dealershipCity}
          value={state.dealershipCity}
          onChange={handleInputChange}
        />
      </div>

      <div className='inputFieldWrapper dealershipState'>
        <PrefilledSelect
          className='select'
          placeholder='State'
          aria-label='dealershipState'
          id='dealershipState'
          name='dealershipState'
          instanceId='dealershipState'
          isLoading={statesFetching}
          options={statesOptions}
          onChange={handleSelectChange}
          value={statesOptions.find(({ value }) => value === state.dealershipState) || null}
          error={errors.dealershipState}
          maxMenuHeight={200}
        />
      </div>

      <div className='inputFieldWrapper dealershipZipCode'>
        {mappedServerErrors.dealershipZipCode && (
          <span className='error'>{mappedServerErrors.dealershipZipCode}</span>
        )}
        <Prefilled
          type='text'
          name='dealershipZipCode'
          placeholder='Postal Code'
          inputMode='numeric'
          error={errors.dealershipZipCode || mappedServerErrors.dealershipZipCode}
          value={state.dealershipZipCode}
          onChange={handleInputChange}
        />
      </div>
      <div className='inputFieldWrapper dealershipUserType'>
        <PrefilledSelect
          className='select'
          placeholder='Dealer User Type'
          aria-label='dealerUserType'
          id='dealerUserType'
          name='dealerUserType'
          instanceId='dealerUserType'
          options={dealerUserTypes}
          onChange={handleSelectChange}
          value={dealerUserTypes.find(({ value }) => value === state.dealerUserType) || null}
          error={errors.dealerUserType}
          menuPlacement='top'
        />
      </div>
      <div>&nbsp;</div>
      <h2>Funding information</h2>
      <div className='inputFieldWrapper funderName'>
        <Prefilled
          type='text'
          name='funderName'
          placeholder='Funder Name'
          value={state.funderName}
          onChange={handleInputChange}
        />
      </div>
      <div className='inputFieldWrapper'>
        <Prefilled
          type='text'
          name='funderAddress'
          placeholder='Address'
          value={state.funderAddress}
          onChange={handleInputChange}
        />
      </div>
      <div className='inputFieldWrapper'>
        <Prefilled
          type='text'
          name='funderCity'
          placeholder='City'
          value={state.funderCity}
          onChange={handleInputChange}
        />
      </div>
      <div className='inputFieldWrapper'>
        <PrefilledSelect
          className='select'
          placeholder='State'
          aria-label='funderState'
          id='funderState'
          name='funderState'
          instanceId='funderState'
          isLoading={statesFetching}
          options={funderStatesOptions}
          onChange={handleSelectChange}
          value={funderStatesOptions.find(({ value }) => value === state.funderState) || null}
          error={errors.funderState}
          maxMenuHeight={200}
        />
      </div>
      <div className='inputFieldWrapper'>
        <Prefilled
          type='text'
          name='funderZipCode'
          placeholder='Postal Code'
          value={state.funderZipCode}
          onChange={handleInputChange}
          error={errors.funderZipCode || mappedServerErrors.funderZipCode}
        />
      </div>
      <div className='inputFieldWrapper'>
        <Prefilled
          type='text'
          name='funderPhoneNumber'
          placeholder='Phone Number'
          value={formatPhoneNumber(state.funderPhoneNumber)}
          onChange={handleInputChange}
          error={errors.funderPhoneNumber}
        />
      </div>
      <div className='inputFieldWrapper'>
        <Prefilled
          type='text'
          name='funderEmail'
          placeholder='E-Mail'
          value={state.funderEmail}
          onChange={handleInputChange}
        />
      </div>

      <div className='error'>You must log out and log back in for changes to take effect</div>
      <br />
      <Button className='saveBtn' onClick={handleButtonClick} disabled={isLoading}>
        {isFormBeingEdited ? 'Save' : 'Close'}
      </Button>

      <style jsx>{`
        .wrapper {
          position: relative;
          width: 43.6rem;
          padding: 2.6rem 1.8rem 4.1rem;
          background: #fff;
          border-radius: 1rem;

          :global(.saveBtn) {
            display: block;
            margin: 0 auto;
          }
        }

        .inputFieldWrapper {
          margin: 0 0 1.7rem;

          :global(.inputContainer > input) {
            background: #fff !important;
          }

          :global(.inputContainer > .edit) {
            background: transparent !important;
          }
        }

        .title {
          margin: 0 0 0.8rem;
          text-align: center;
          font-size: 3.2rem;
        }

        .description {
          max-width: 32rem;
          margin: 0 auto 2.3rem;
          text-align: center;
          font-size: 1.6rem;
        }

        .inputFieldWrapper {
          position: relative;
        }

        .error {
          display: block;
          margin: 0 0 1rem;
          color: var(--alert-color);
          font-size: 1.4rem;
        }

        .email {
          :global(input.email) {
            color: var(--secondary-text-color) !important;
          }

          :global(.adornment) {
            button {
              background: transparent;
              color: var(--primary-color);
              font-size: 1.6rem;
            }
          }
        }

        @media (max-width: 1024px) {
          .wrapper {
            width: 100%;
            height: 100%;
            padding: 2.7rem 1.5rem 2.3rem;

            :global(.saveBtn) {
              position: fixed;
              bottom: 2.1rem;
              left: 1.2rem;
              width: calc(100vw - 2.4rem);
            }
          }

          .title {
            line-height: 2.4rem;
            font-size: 1.8rem;
          }

          .description {
            margin: 0.2rem auto 3rem;
            line-height: 1.9rem;
            font-size: 1.4rem;
          }
        }
      `}</style>
    </div>
  )
}

function stripPhoneNumber(phoneNumber) {
  if (phoneNumber && phoneNumber.length > 0) {
    return phoneNumber.replace(/[^\d]/g, '')
  }

  return ''
}

function validate(state) {
  const errors = {}
  let isValid = true

  if (state.dealershipName.length < 1) errors['dealershipName'] = true
  if (stripPhoneNumber(state.phoneNumber).length !== 10) errors['phoneNumber'] = true
  if (state.dealershipAddress.length < 1) errors['dealershipAddress'] = true
  if (state.dealershipCity.length < 1) errors['dealershipCity'] = true
  if (state.dealershipState.length < 1) errors['dealershipState'] = true
  if (state.dealershipZipCode.length < 1) errors['dealershipZipCode'] = true
  if (
    stripPhoneNumber(state.funderPhoneNumber).length > 0 &&
    stripPhoneNumber(state.funderPhoneNumber).length !== 10
  )
    errors['funderPhoneNumber'] = true
  if (state?.funderZipCode?.length > 0 && state?.funderZipCode?.length !== 5)
    errors['funderZipCode'] = true

  if (Object.entries(errors).length > 0) isValid = false

  return { isValid, errors }
}

export function mapStatesOptions(states) {
  if (!states) return []

  return states.map(({ id, name }) => ({ value: name, label: name }))
}

export function mapFunderStatesOptions(funderStates) {
  if (!funderStates) return []

  return funderStates.map(({ id, name }) => ({ value: name, label: name }))
}

function mapServerErrors(errors) {
  if (!errors) return {}

  const mappedErrors = {}

  errors.forEach((error) => {
    switch (+error?.code) {
      case APIErrors.InvalidZipCode:
      case APIErrors.InvalidGoogleApiResponseStatusCode:
      case APIErrors.TimezoneNotFound:
      case APIErrors.TimezoneNotSupported:
      case APIErrors.ZipCodeDoesNotExists:
        mappedErrors['zipCode'] = errorMessages.invalidZipCode
        break
      case APIErrors.CountryNotSupported:
        mappedErrors['password'] = errorMessages.countryNotSupported
        break
      default:
        mappedErrors[APIErrors.Unknown] = error.message
        break
    }
  })

  return mappedErrors
}
